import { apiClient } from './api-client';

/**
 * Available credits response from AI Core
 */
export interface CreditsInfo {
  available_credits: number;
  user_email: string;
  request_id: string;
  subscription?: {
    status: string;
    subscription_id: string;
    billing_period: string;
    billing_interval: number;
    next_payment_date: string;
    features: string[];
  };
}

/**
 * AI Credits Service
 * Handles fetching and displaying user's available AI credits from AI Core
 */
export const aiCreditsService = {
  /**
   * Get available credits for the authenticated user
   *
   * @param formId Form ID (required for API client)
   * @returns Promise<CreditsInfo>
   */
  async getAvailableCredits(formId: number): Promise<CreditsInfo> {
    return apiClient.callProxy('aICredits', 'getCredits', {
      formId,
    });
  },

  /**
   * Check if user has sufficient credits for an operation
   *
   * @param formId Form ID
   * @param creditsNeeded Number of credits needed
   * @returns Promise<{ sufficient: boolean; available: number; needed: number }>
   */
  async checkSufficientCredits(
    formId: number,
    creditsNeeded: number
  ): Promise<{ sufficient: boolean; available: number; needed: number }> {
    const creditsInfo = await this.getAvailableCredits(formId);

    return {
      sufficient: creditsInfo.available_credits >= creditsNeeded,
      available: creditsInfo.available_credits,
      needed: creditsNeeded,
    };
  },

  /**
   * Format subscription info for display
   *
   * @param creditsInfo Credits information object
   * @returns Formatted subscription string or null
   */
  formatSubscriptionInfo(creditsInfo: CreditsInfo): string | null {
    if (!creditsInfo.subscription) {
      return null;
    }

    const { status, billing_period, billing_interval, next_payment_date } = creditsInfo.subscription;

    let periodString = billing_period;
    if (billing_interval > 1) {
      periodString = `${billing_interval} ${billing_period}s`;
    }

    let statusString = '';
    if (status === 'active') {
      statusString = `Active subscription (${periodString})`;
      if (next_payment_date) {
        const nextDate = new Date(next_payment_date).toLocaleDateString();
        statusString += ` - Next payment: ${nextDate}`;
      }
    } else {
      statusString = `Subscription ${status}`;
    }

    return statusString;
  },
};

